import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { defaultSchema } from 'rehype-sanitize';
import rehypeSanitize from 'rehype-sanitize';
import { ExternalLink, ShoppingCart, Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

/**
 * Composant pour afficher un bloc produit affilié Amazon
 * Optimisé pour un affichage responsive (mobile-first)
 */
const AffiliateProduct = ({ title, description, price, image, link, rating }) => {
  return (
    <Card className="my-6 border-2 border-honey/30 bg-honey/5 hover:shadow-lg transition-all overflow-hidden">
      <CardContent className="p-0">
        {/* Layout Desktop : Horizontal | Mobile : Vertical */}
        <div className="flex flex-col md:flex-row">
          
          {/* Image du produit */}
          <div className="w-full md:w-1/3 flex-shrink-0">
            <img 
              src={image} 
              alt={title}
              className="w-full h-48 md:h-full object-cover"
            />
          </div>
          
          {/* Contenu principal */}
          <div className="flex-1 p-4 md:p-6 flex flex-col">
            {/* Titre */}
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 leading-tight">
              {title}
            </h3>
            
            {/* Note avec étoiles */}
            {rating && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      fill={i < rating ? '#F6C90E' : 'none'}
                      stroke={i < rating ? '#F6C90E' : '#D1D5DB'}
                      className="h-4 w-4"
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600">({rating}/5)</span>
              </div>
            )}
            
            {/* Description */}
            <p className="text-sm md:text-base text-gray-600 mb-4 flex-grow">
              {description}
            </p>
            
            {/* Prix + Bouton CTA - Layout en colonne sur mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-auto">
              <div className="text-2xl font-bold text-honey whitespace-nowrap">
                {price}
              </div>
              <Button 
                className="w-full sm:w-auto bg-honey hover:bg-honey/90 text-gray-900 font-semibold"
                onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Voir sur Amazon
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Disclaimers - Toujours en bas */}
        <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 bg-gray-50/50 space-y-1.5">
          <p className="text-xs text-gray-500 italic">
            💡 <strong>Prix indicatif :</strong> Le prix affiché peut évoluer. Vérifiez le prix actuel sur Amazon avant l'achat.
          </p>
          <p className="text-xs text-gray-500 italic">
            🔗 <strong>Lien affilié :</strong> En tant que Partenaire Amazon, nous percevons une rémunération sur les achats qualifiés.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Composant pour un bloc CTA (Call-to-Action)
 */
const CTABlock = ({ title, description, buttonText, buttonLink, variant = 'primary' }) => {
  const bgColor = variant === 'primary' ? 'bg-sage/10' : 'bg-honey/10';
  const borderColor = variant === 'primary' ? 'border-sage' : 'border-honey';
  const buttonColor = variant === 'primary' ? 'bg-sage hover:bg-sage/90' : 'bg-honey hover:bg-honey/90';
  
  return (
    <Card className={`my-6 border-2 ${borderColor} ${bgColor}`}>
      <CardContent className="p-6 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">{description}</p>
        <Button 
          className={`${buttonColor} text-white font-semibold`}
          onClick={() => window.open(buttonLink, '_blank', 'noopener,noreferrer')}
        >
          {buttonText}
          <ExternalLink className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Composant pour une citation/conseil
 */
const TipBox = ({ children, type = 'info' }) => {
  const config = {
    info: { bg: 'bg-blue-50', border: 'border-blue-400', icon: 'ℹ️' },
    tip: { bg: 'bg-honey/10', border: 'border-honey', icon: '💡' },
    warning: { bg: 'bg-orange-50', border: 'border-orange-400', icon: '⚠️' },
    success: { bg: 'bg-green-50', border: 'border-green-400', icon: '✅' }
  };
  
  const { bg, border, icon } = config[type] || config.info;
  
  return (
    <div className={`my-6 p-6 rounded-lg border-l-4 ${border} ${bg}`}>
      <div className="flex gap-3">
        <div className="text-2xl">{icon}</div>
        <div className="flex-1 prose prose-sm max-w-none">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Composant principal pour rendre le contenu Markdown enrichi
 */
export const RichArticleContent = ({ content }) => {
  // Configuration de sanitize pour autoriser nos attributs personnalisés
  const sanitizeConfig = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      div: [
        ...(defaultSchema.attributes.div || []),
        'dataType', 'dataTitle', 'dataDescription', 'dataPrice', 
        'dataImage', 'dataLink', 'dataRating', 'dataButtonText', 
        'dataButtonLink', 'dataVariant', 'dataTipType',
        // Avec tirets aussi
        ['data*']
      ],
    },
  };

  // Composants personnalisés pour le rendu Markdown
  const components = {
    // Blocs personnalisés via attributs data-*
    div: ({ node, children, ...props }) => {
      // Normaliser les noms d'attributs (data-type ou dataType)
      const dataType = props['data-type'] || props['dataType'];
      
      // Bloc produit affilié
      if (dataType === 'affiliate-product') {
        return (
          <AffiliateProduct
            title={props['data-title'] || props['dataTitle']}
            description={props['data-description'] || props['dataDescription']}
            price={props['data-price'] || props['dataPrice']}
            image={props['data-image'] || props['dataImage']}
            link={props['data-link'] || props['dataLink']}
            rating={parseInt(props['data-rating'] || props['dataRating'] || 0)}
          />
        );
      }
      
      // Bloc CTA
      if (dataType === 'cta') {
        return (
          <CTABlock
            title={props['data-title'] || props['dataTitle']}
            description={props['data-description'] || props['dataDescription']}
            buttonText={props['data-button-text'] || props['dataButtonText']}
            buttonLink={props['data-button-link'] || props['dataButtonLink']}
            variant={props['data-variant'] || props['dataVariant']}
          />
        );
      }
      
      // Bloc conseil/tip
      if (dataType === 'tip') {
        return (
          <TipBox type={props['data-tip-type'] || props['dataTipType']}>
            {children}
          </TipBox>
        );
      }
      
      return <div {...props}>{children}</div>;
    },
    
    // Personnalisation des éléments HTML standard
    h1: ({ children }) => (
      <h1 className="text-4xl font-serif font-bold text-gray-900 mt-8 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-3xl font-serif font-bold text-gray-900 mt-8 mb-4">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-2xl font-serif font-bold text-gray-900 mt-6 mb-3">{children}</h3>
    ),
    p: ({ children }) => (
      <p className="text-lg text-gray-700 leading-relaxed mb-4">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700">{children}</ol>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-sage pl-6 py-2 my-6 italic text-gray-600 bg-sage/5">
        {children}
      </blockquote>
    ),
    img: ({ src, alt }) => (
      <img 
        src={src} 
        alt={alt} 
        className="w-full rounded-lg shadow-lg my-6"
      />
    ),
    a: ({ href, children }) => (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-sage hover:text-sage/80 font-semibold underline"
      >
        {children}
      </a>
    ),
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <code className="px-2 py-1 bg-gray-100 text-honey rounded text-sm font-mono">
            {children}
          </code>
        );
      }
      return (
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4">
          <code className="text-sm font-mono">{children}</code>
        </pre>
      );
    },
    table: ({ children }) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full border-collapse border border-gray-300">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 bg-sage/10 px-4 py-2 text-left font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 px-4 py-2">{children}</td>
    ),
  };

  return (
    <div className="prose prose-lg max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeConfig]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default RichArticleContent;